// ============================================================
// Tome System Logic — Rogue-like drop & pick with build limit
// ============================================================
import { TOME_DEFINITIONS, TOME_IDS } from '../constants/tomes.js';
/** Max number of unique tome types a player can pick per run (6 of 16) */
export const MAX_TOME_TYPES = 6;
/** Get the current level of a tome (0 if not found) */
export function getTomeLevel(tomes, id) {
    const t = tomes.find((t) => t.id === id);
    return t ? t.level : 0;
}
/** Round a number to at most 4 decimal places to eliminate floating-point noise */
function round4(v) {
    return Math.round(v * 10000) / 10000;
}
/** Get the effective bonus value for a tome. Clamps the effective level to the tome's
 *  current maxLevel so that rebalancing a tome downward (e.g. Bloody Book 99 → 25) takes
 *  effect for existing saves whose stored level may exceed the new cap. */
export function getTomeBonus(tomes, id) {
    const def = TOME_DEFINITIONS[id];
    const level = Math.min(getTomeLevel(tomes, id), def.maxLevel);
    return round4(level * def.bonusPerLevel);
}
/** Check if all chosen tomes have reached their max level (no more drops needed) */
export function allChosenTomesMaxed(tomes, chosenTypes) {
    if (chosenTypes.length === 0)
        return false;
    // Once 4 types are locked in, check if all 4 are maxed
    // If fewer than 4 chosen, there's always room for a new type
    if (chosenTypes.length < MAX_TOME_TYPES)
        return false;
    return chosenTypes.every((id) => getTomeLevel(tomes, id) >= TOME_DEFINITIONS[id].maxLevel);
}
/** Initialize default tome states (all at level 0, unlocked for picking) */
export function createDefaultTomes() {
    return TOME_IDS.map((id) => ({ id, level: 0, unlocked: true }));
}
/** Add +1 level to a tome. Returns the new level. On the first pick of Book of the
 *  Archivist, the resist school is randomly locked in — subsequent ranks keep feeding
 *  +0.2% to that same school. */
export function pickTome(tomes, id) {
    let tome = tomes.find((t) => t.id === id);
    if (!tome) {
        tome = { id, level: 0, unlocked: true };
        tomes.push(tome);
    }
    const def = TOME_DEFINITIONS[id];
    if (tome.level < def.maxLevel) {
        tome.level += 1;
    }
    if (id === 'book_archivist') {
        // Random per-pick spread — each rank rolls ONE of the four schools and adds +0.2 %
        // to that school's accumulator. No more "locked school on first pick" — the player
        // can end up with an uneven spread across fire / frost / shadow / nature.
        if (!tome.archivistResists) {
            tome.archivistResists = { fire: 0, frost: 0, shadow: 0, nature: 0 };
        }
        const schools = ['fire', 'frost', 'shadow', 'nature'];
        const picked = schools[Math.floor(Math.random() * schools.length)];
        tome.archivistResists[picked] = Math.min(20, tome.archivistResists[picked] + 0.2);
    }
    return tome.level;
}
/** Book of the Archivist — resolve the per-pick random resist spread. Each rank adds
 *  +0.2 % to a randomly-rolled school on pick, accumulated in `tome.archivistResists`.
 *  Returns the per-school totals (each capped at 20 %). Legacy saves whose tome only
 *  stores the old single-school `archivistResist` field get their full level × 0.2 %
 *  attributed to that one school for back-compat. */
export function getArchivistResistBonus(tomes) {
    const t = tomes.find((x) => x.id === 'book_archivist');
    const bonus = { fire: 0, frost: 0, shadow: 0, nature: 0 };
    if (!t || t.level <= 0)
        return bonus;
    if (t.archivistResists) {
        bonus.fire = Math.min(20, t.archivistResists.fire);
        bonus.frost = Math.min(20, t.archivistResists.frost);
        bonus.shadow = Math.min(20, t.archivistResists.shadow);
        bonus.nature = Math.min(20, t.archivistResists.nature);
        return bonus;
    }
    if (t.archivistResist) {
        bonus[t.archivistResist] = Math.min(20, t.level * 0.2);
    }
    return bonus;
}
/**
 * Roll 3 random tomes for the player to choose from (no duplicates).
 * Respects the 4-tome build limit:
 * - If < 4 types chosen: pool = already chosen + unchosen (all 6)
 * - If = 4 types chosen: pool = only the 4 chosen types
 * This means once you pick 4 different tomes, the other 2 are locked forever.
 */
export function rollTomeChoices(chosenTypes, count = 3, tomes, 
/** Predicate consulted to filter out quest-gated tomes that haven't been unlocked yet.
 *  Omit to treat every gated tome as unlocked (safe default for tests). */
isUnlocked = () => true, 
/** Pre-run banned tomes (from BookBanScene). Never offered during rolls. */
bannedTomes = []) {
    let pool;
    if (chosenTypes.length >= MAX_TOME_TYPES) {
        // Locked in — only roll from the already-chosen types
        pool = [...chosenTypes];
    }
    else {
        // Still open — every tome eligible
        pool = [...TOME_IDS];
    }
    // Drop banned tomes (pre-run ban choice) — they never surface as a roll option.
    if (bannedTomes.length > 0) {
        const bannedSet = new Set(bannedTomes);
        pool = pool.filter((id) => !bannedSet.has(id));
    }
    // Remove any tomes already at max level — no point offering them
    if (tomes) {
        pool = pool.filter((id) => getTomeLevel(tomes, id) < TOME_DEFINITIONS[id].maxLevel);
    }
    // Drop quest-gated tomes whose unlock requirement isn't met.
    pool = pool.filter((id) => {
        const def = TOME_DEFINITIONS[id];
        if (!def.requiresQuestId)
            return true;
        return isUnlocked(id);
    });
    const choices = [];
    for (let i = 0; i < count && pool.length > 0; i++) {
        const idx = Math.floor(Math.random() * pool.length);
        choices.push(pool.splice(idx, 1)[0]);
    }
    return choices;
}
// ============================================================
// Talent System — spell-effect upgrades every 5 hero levels
// ============================================================
/** Levels between each talent point */
export const TALENT_LEVEL_INTERVAL = 5;
/** Compute cumulative bonus from `perLevel` with optional exponential doubling. */
function computeBonus(rank, perLevel, doubling) {
    if (rank <= 0)
        return 0;
    if (doubling) {
        // Sum of geometric series: perLevel * (2^rank - 1) — rounded to kill float noise
        return round4(perLevel * (Math.pow(2, rank) - 1));
    }
    return round4(rank * perLevel);
}
/**
 * Returns a human-readable string describing exactly what the NEXT rank point gives,
 * e.g. "+15% base damage" or "+1 extra target".
 *
 * Uses `nextRankUnit` from TalentUpgradeInfo to format the incremental value.
 * For split-phase talents, returns the phase-2 label once past splitAtRank.
 */
export function getNextRankLabel(abilityId, currentRank) {
    const info = TALENT_UPGRADES[abilityId];
    if (!info)
        return '';
    // One-time unlock: no numeric label needed
    if (info.maxRank === 1)
        return info.label;
    // Thunder-strike phase 2
    const inPhase2 = info.splitAtRank !== undefined && currentRank >= info.splitAtRank;
    if (inPhase2 && info.perLevel2 !== undefined) {
        const phase2Rank = currentRank - info.splitAtRank;
        const increment = computeBonus(phase2Rank + 1, info.perLevel2, info.doubling2)
            - computeBonus(phase2Rank, info.perLevel2, info.doubling2);
        // At the exact rank-to-splitAtRank crossover, prefer the dedicated transition
        // label so the user reads "Crushing Finisher unlock" instead of "+5% damage".
        const isTransition = currentRank === info.splitAtRank;
        const unit = (isTransition && info.phase2TransitionUnit)
            ? info.phase2TransitionUnit
            : (info.nextRankUnit2 ?? info.label2 ?? '');
        return formatIncrement(increment, unit);
    }
    // Phase 1
    const unit = info.nextRankUnit;
    if (!unit)
        return info.label; // fallback to static label
    const increment = computeBonus(currentRank + 1, info.perLevel, info.doubling)
        - computeBonus(currentRank, info.perLevel, info.doubling);
    return formatIncrement(increment, unit);
}
function formatIncrement(increment, unit) {
    // ': '-prefix → literal description (no "+N" prefix). Used for unlock-style
    // ranks where the increment value isn't meaningful to the player.
    if (unit.startsWith(': ')) {
        return unit.slice(2);
    }
    // '%'-prefix → multiply by 100, show up to 1 decimal (strips .0)
    if (unit.startsWith('%')) {
        const pct = round4(increment * 100);
        const str = Number.isInteger(pct) ? String(pct) : pct.toFixed(1).replace(/\.0$/, '');
        return `+${str}${unit}`;
    }
    // 's'-prefix → divide by 1000, format with 1 decimal
    if (unit.startsWith('s')) {
        const secs = (increment / 1000).toFixed(1).replace(/\.0$/, '');
        return `+${secs}${unit}`;
    }
    // Integer or float — round to at most 2 decimal places
    const val = Math.round(increment * 100) / 100;
    return `+${val}${unit}`;
}
/**
 * Returns a human-readable label for the TOTAL accumulated bonus at the given rank,
 * using the same unit format as getNextRankLabel.
 * e.g. rank 2 fireball → "+45% base damage" (cumulative)
 */
export function getTalentTotalLabel(abilityId, rank) {
    const info = TALENT_UPGRADES[abilityId];
    if (!info || rank <= 0)
        return '';
    if (info.maxRank === 1)
        return info.label;
    // Phase 1 portion
    const phase1Ranks = info.splitAtRank !== undefined ? Math.min(rank, info.splitAtRank) : rank;
    const unit = info.nextRankUnit ?? '';
    const phase1Total = computeBonus(phase1Ranks, info.perLevel, info.doubling);
    let result = unit ? formatIncrement(phase1Total, unit) : `${info.label}`;
    // Phase 2 portion (split talents like thunder_strike)
    if (info.splitAtRank !== undefined && rank > info.splitAtRank && info.perLevel2 !== undefined) {
        const phase2Ranks = rank - info.splitAtRank;
        const phase2Total = computeBonus(phase2Ranks, info.perLevel2, info.doubling2);
        const unit2 = info.nextRankUnit2 ?? '';
        if (unit2)
            result += `, ${formatIncrement(phase2Total, unit2)}`;
    }
    return result;
}
/**
 * Per-ability talent upgrade definitions.
 *
 * Doubling talents: damage/DoT bonuses double each rank so they keep up with
 * late-game difficulty.  Utility talents (stun, shield, duration, targets) stay flat.
 */
export const TALENT_UPGRADES = {
    // ── Warrior (Goomar) ──
    // maxRank 6: up to +6 extra targets; stun capped at 6s
    whirlwind: { label: '+8% Whirlwind damage (doubling)', stat: 'damageMult', perLevel: 0.08, nextRankUnit: '% damage', doubling: true, maxRank: 6 },
    earthquake: { label: '+0.5s stun duration', stat: 'duration', perLevel: 500, nextRankUnit: 's stun', maxRank: 6 },
    // maxRank 6 — doubling on speed is most dangerous
    berserk: { label: '+crit dmg & atk spd (doubling)', stat: 'berserkBonus', perLevel: 0.04, nextRankUnit: '% crit dmg & spd', doubling: true, maxRank: 6 },
    undying_fury: { label: '+5% revive HP per rank', stat: 'reviveHp', perLevel: 0.05, nextRankUnit: '% revive HP', maxRank: 6 },
    // ── Tank / Paladin (Ironheld) ──
    challenge: { label: 'Force all nearby enemies to attack Ironheld', stat: 'aoeChallenge', perLevel: 1, maxRank: 1 },
    resurrection: { label: '+10% revive HP', stat: 'reviveHp', perLevel: 0.10, nextRankUnit: '% revive HP', maxRank: 6 },
    // Consecration: trimmed perLevel 0.24 → 0.15 doubling × 6 to match Thornlash's
    // damage-spec scaling. Was the highest talent ceiling in the roster (+1512%);
    // even with paladins running low SP, the multiplier was outscaling every
    // other AoE in the game. New cap = 0.15 × (2^6 − 1) = +945 %.
    consecration: { label: '+15% AoE damage (doubling)', stat: 'bonusDamage', perLevel: 0.15, nextRankUnit: '% AoE dmg', doubling: true, maxRank: 6 },
    divine_reflect: { label: '+5% reflect', stat: 'reflectPct', perLevel: 0.05, nextRankUnit: '% reflect', maxRank: 4 },
    // ── Mage (Zyphara) ──
    // Fireball — two-phase: ranks 1-4 scale the burn DoT (existing behavior),
    // ranks 5-8 scale direct damage so casters keep up at high stage. Without
    // the phase-2 damageMult, the burn was the only thing scaling and the burn
    // gets overwritten on every recast — direct hit was effectively flat.
    fireball: {
        label: 'Ranks 1-4: +12% burn damage (doubling)',
        stat: 'dotFraction',
        perLevel: 0.12,
        nextRankUnit: '% burn damage',
        doubling: true,
        splitAtRank: 4,
        stat2: 'damageMult',
        perLevel2: 0.10,
        doubling2: true,
        label2: 'Rank 5+: +10% Fireball damage (doubling)',
        nextRankUnit2: '% damage',
        maxRank: 8,
    },
    // Eruption — same shape as Fireball: burn ramp first, then direct AoE damage.
    eruption: {
        label: 'Ranks 1-4: +8% burn damage (doubling)',
        stat: 'dotFraction',
        perLevel: 0.08,
        nextRankUnit: '% burn damage',
        doubling: true,
        splitAtRank: 4,
        stat2: 'damageMult',
        perLevel2: 0.06,
        doubling2: true,
        label2: 'Rank 5+: +6% Eruption damage (doubling)',
        nextRankUnit2: '% damage',
        maxRank: 8,
    },
    arcane_might: { label: '+mana regen per tick', stat: 'resistBonus', perLevel: 0.01, nextRankUnit: '% all resist', maxRank: 8 },
    magma_shield: { label: '+0.2s stun duration', stat: 'duration', perLevel: 200, nextRankUnit: 's stun', maxRank: 5 },
    // ── Archer (Sylvari) ──
    // Multishot — two-phase: ranks 1-3 add targets, ranks 4-6 scale damage (doubling)
    multishot: {
        label: 'Ranks 1-3: +1 extra target',
        stat: 'targets',
        perLevel: 1,
        nextRankUnit: ' extra target',
        splitAtRank: 3,
        stat2: 'damageMult',
        perLevel2: 0.12,
        doubling2: true,
        label2: 'Rank 4+: +12% damage (doubling)',
        nextRankUnit2: '% damage',
        maxRank: 6,
    },
    vine_snare: { label: '+16% trap damage per rank', stat: 'damageMult', perLevel: 0.16, nextRankUnit: '% damage', maxRank: 3 },
    piercing_shot: { label: '+2s stun on hit', stat: 'stunDuration', perLevel: 2000, nextRankUnit: 's stun', maxRank: 3 },
    eagle_eye: { label: 'Buff all allies', stat: 'groupBuff', perLevel: 1, maxRank: 1 },
    // ── Healer (Lumiel) ──
    fear: { label: '+16% chance to fear nearby enemies per rank', stat: 'extraFearChance', perLevel: 0.16, nextRankUnit: '% chance to fear nearby enemies', maxRank: 5 },
    // Holy Shield: nerfed from 0.5×6 (max +300% / ×4 mult) → 0.2×5 (max
    // +100% / ×2 mult) per the balance pass. The cast formula in
    // BattleScene was also retuned (200+8×SP → 100+3×SP) so the talent's
    // role is to roughly double the bubble at full investment, not to
    // 4× it on top of an already-bloated base.
    shield: { label: '+20% shield HP per rank', stat: 'shieldMult', perLevel: 0.2, nextRankUnit: '% shield HP', maxRank: 5 },
    word_of_death: { label: '+12% damage per rank', stat: 'damageMult', perLevel: 0.12, nextRankUnit: '% damage', maxRank: 6 },
    bless: { label: '+1 heal target per rank', stat: 'targets', perLevel: 1, maxRank: 2 },
    // ── Rogue (Kael) ──
    // Sinister Strike — ranks 1-3 scale poison DoT, ranks 4-6 scale direct damage
    // (mirrors the Fireball/Eruption fix so non-warrior dotFraction-only abilities
    // get a damage scaling lever at high rank).
    sinister_strike: {
        label: 'Ranks 1-3: +8% poison damage (doubling)',
        stat: 'dotFraction',
        perLevel: 0.08,
        nextRankUnit: '% poison damage',
        doubling: true,
        splitAtRank: 3,
        stat2: 'damageMult',
        perLevel2: 0.12,
        doubling2: true,
        label2: 'Rank 4+: +12% Sinister Strike damage (doubling)',
        nextRankUnit2: '% damage',
        maxRank: 6,
    },
    // Poison Cloud — same shape as Sinister Strike.
    poison_cloud: {
        label: 'Ranks 1-3: +8% poison damage (doubling)',
        stat: 'dotFraction',
        perLevel: 0.08,
        nextRankUnit: '% poison damage',
        doubling: true,
        splitAtRank: 3,
        stat2: 'damageMult',
        perLevel2: 0.10,
        doubling2: true,
        label2: 'Rank 4+: +10% Poison Cloud damage (doubling)',
        nextRankUnit2: '% damage',
        maxRank: 6,
    },
    wraith_strike: { label: '+12% Dirty Blades damage (doubling)', stat: 'damageMult', perLevel: 0.12, nextRankUnit: '% damage', doubling: true, maxRank: 6 },
    // ── Amazon (Zayla) ──
    leap_strike: { label: '+12% Leap Strike damage (doubling)', stat: 'damageMult', perLevel: 0.12, nextRankUnit: '% damage', doubling: true, maxRank: 6 },
    freezing_shot: { label: '+1s freeze duration', stat: 'duration', perLevel: 1000, nextRankUnit: 's freeze', maxRank: 4 },
    war_cry: { label: '+10% attack speed & +2s duration per rank', stat: 'atkSpdMult', perLevel: 0.10, nextRankUnit: '% attack speed', maxRank: 3 },
    shuriken_barrage: { label: '+2 shurikens', stat: 'targets', perLevel: 2, nextRankUnit: ' shurikens', maxRank: 5 },
    evasion: { label: '+1s evasion duration', stat: 'duration', perLevel: 1000, nextRankUnit: 's evasion', maxRank: 8 },
    // ── Dwarf (Lootfi) ──
    stone_form: { label: '+1s stone form duration', stat: 'duration', perLevel: 1000, nextRankUnit: 's stone form', maxRank: 8 },
    /**
     * Thunder Strike — two-phase talent (maxRank 7 = 3 phase-1 + 4 phase-2):
     *   Ranks 1-3 : +30% chance to double-strike each rank (max 90%)
     *   Rank  4-7 : +30%→60%→120%→240% bonus Thunder Strike damage (doubling each rank)
     */
    thunder_strike: {
        label: 'Ranks 1-3: +30% double-strike chance',
        stat: 'doubleStrike',
        perLevel: 0.30,
        nextRankUnit: '% double-strike chance',
        splitAtRank: 3,
        stat2: 'thunderDmg',
        perLevel2: 0.24,
        doubling2: true,
        label2: 'Rank 4+: Thunder Strike dmg (doubling)',
        nextRankUnit2: '% Thunder Strike dmg',
        maxRank: 7,
    },
    // Mace Throw: damage-doubling spec to match Shield Bash / Leap Strike /
    // Wraith Strike shape — heavy single-target melee with utility (daze).
    // Cumulative growth at rank 6 = 10 × (2^6 − 1) = +630 %.
    mace_throw: { label: '+10% Mace Throw damage (doubling)', stat: 'damageMult', perLevel: 0.10, doubling: true, nextRankUnit: '% damage', maxRank: 6 },
    // ── Valkyrie (Valka) ──
    // Shield Bash: damage-doubling spec starting at +10 % rank 1 (was 15; user
    // retuned). Cumulative growth at rank 6 = 10 × (2^6 − 1) = +630 %.
    shield_bash_charge: { label: '+10% Shield Bash damage (doubling)', stat: 'damageMult', perLevel: 0.10, doubling: true, nextRankUnit: '% damage', maxRank: 6 },
    // Tempest Slam: damageMult doubling for ranks 1-3, rank 4 unlocks
    // Crushing Finisher (stuns for 1.5 s if Whirlwind hit ≥ 4 enemies). Ranks 5-6
    // keep growing damageMult so the stun unlock doesn't feel like the only payoff.
    whirlwind_bludgeon: {
        label: 'Ranks 1-3: +15% damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.15,
        doubling: true,
        nextRankUnit: '% damage',
        splitAtRank: 3,
        // Phase-2 stat encodes BOTH the Crushing Finisher unlock (value > 0
        // means rank ≥ 4 → stun unlocked) AND a per-rank damage scaling that
        // adds to wbDmgMult. Read in BattleScene's whirlwind_bludgeon handler.
        stat2: 'crushingFinisher',
        perLevel2: 0.30,
        label2: 'Rank 4+: Crushing Finisher (1.5 s stun on ≥ 4 mobs) + 30% damage per rank',
        // Crossover (rank 3 → 4): unlock + first 30 % damage bump.
        phase2TransitionUnit: ': Crushing Finisher unlock (1.5 s stun on ≥ 4 mobs) + 30% damage',
        // Ranks 4 → 5 → 6: each rank adds another flat 30 % damage on top.
        nextRankUnit2: '% damage',
        maxRank: 6,
    },
    // Banner of Valor: flat +10% buff strength per rank (damageMult used loosely
    // as a label-friendly scale — actual effect hooked via talentStat read).
    plant_banner: { label: '+2s Banner duration per rank', stat: 'duration', perLevel: 2000, nextRankUnit: 's duration', maxRank: 5 },
    // Counter-Strike: rank 1 = +15% chance (45% total melee), rank 2 = unlock
    // counters on ranged attacks, ranks 3-6 = +5% counter-chance per rank so
    // an end-build Valka is countering ~65% of hits with custom VFX variant.
    // Counter-Strike: non-uniform per-rank curve, computed inline in
    // BattleScene. Rank 1 +15 %, rank 2 ranged unlock, ranks 3-6 +5 % each.
    // End-build rank 6 = 30 % base + 15 % + 4×5 % = 70 % counter chance.
    // The split-talent shape here is for the UI tooltip; the actual chance
    // is read directly from the rank in combat (see BattleScene Counter-
    // Strike branch in applyDamageToHero).
    counter_strike: {
        label: 'Rank 1: +15% counter chance · Rank 2: ranged unlock · Ranks 3-6: +5% per rank',
        stat: 'chanceBonus',
        perLevel: 0.15,
        nextRankUnit: '% counter chance',
        splitAtRank: 1,
        stat2: 'rangedUnlock',
        // perLevel2 is purely cosmetic — actual chance is computed inline in
        // BattleScene's Counter-Strike branch by reading `talentBonuses['counter_strike']`
        // directly. 0.05 here just makes the per-rank tooltip read "+5% counter chance".
        perLevel2: 0.05,
        label2: 'Rank 2+: counters fire on ranged attackers',
        // Crossover (rank 1 → 2): unlock + the new per-rank scaling.
        phase2TransitionUnit: ': Ranged unlock + 5% counter chance',
        // Standard phase-2 ranks (2 → 3..6): just the per-rank chance bump.
        nextRankUnit2: '% counter chance',
        maxRank: 6,
    },
    // ── Necromancer (Morveth) ──
    // death_coil: ranks 1-5 +12% doubling damage (+372% cap at rank 5); rank 6
    // unlocks Plague — bolt rots enemies behind the target. Mirrors Thornlash's
    // shape so Morveth's primary nuke scales like comparable caster spells.
    death_coil: {
        label: 'Ranks 1-5: +12% Death Coil damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.12,
        nextRankUnit: '% damage',
        doubling: true,
        splitAtRank: 5,
        stat2: 'plagueUnlock',
        perLevel2: 1,
        label2: 'Rank 6: Plague spreads and rots enemies behind target',
        nextRankUnit2: ' (Plague unlock)',
        maxRank: 6,
    },
    // raise_dead: +15% minion HP/dmg per rank; rank 7 = max (unlocks Corpse Explosion on minion death)
    raise_dead: {
        label: 'Ranks 1-6: +15% minion HP & damage',
        stat: 'minionBuff',
        perLevel: 0.15,
        nextRankUnit: '% minion stats',
        splitAtRank: 6,
        stat2: 'corpseExplosion',
        perLevel2: 1,
        label2: 'Rank 7: Corpse Explosion on minion death (cannot unlock with Lich Form)',
        nextRankUnit2: ' (Corpse Explosion unlock)',
        maxRank: 7,
    },
    // soul_leech: each rank adds +1 to heal multiplier (souls × (1+rank)); rank 7 = max (unlocks Lich Form)
    soul_leech: {
        label: 'Ranks 1-6: +30% HP regen per rank (base: 0.01% maxHP/s per soul)',
        stat: 'healPerKill',
        perLevel: 1,
        nextRankUnit: ' heal multiplier',
        splitAtRank: 6,
        stat2: 'lichForm',
        perLevel2: 1,
        label2: 'Rank 7: Lich Form — 3s transformation (cannot unlock with Corpse Explosion)',
        nextRankUnit2: ' (Lich Form unlock)',
        maxRank: 7,
    },
    // bone_shield: knockback fires baseline now (Morveth's only peel — no
    // longer talent-gated). Rank 1 talent (`knockbackUnlock` stat name kept
    // for save-data compat) instead arms the bone wave with damage on hit.
    // Ranks 2+ scale shield HP via the split phase-2 stat.
    bone_shield: {
        label: 'Rank 1: Bone Wave deals (40 + 1×SP) damage on knockback',
        stat: 'knockbackUnlock',
        perLevel: 1,
        nextRankUnit: ' (Bone Wave damage)',
        splitAtRank: 1,
        stat2: 'shieldMult',
        perLevel2: 0.30,
        label2: 'Ranks 2+: +30% shield HP per rank',
        nextRankUnit2: '× shield HP',
        maxRank: 5,
    },
    // ── Druid (Thorne) ──
    // thornlash: ranks 1-5 +15 % doubling damage; rank 6 unlocks Verdant Spread —
    // the bleed jumps to the 2 nearest enemies on application.
    thornlash: {
        label: 'Ranks 1-5: +15% Thornlash damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.15,
        nextRankUnit: '% damage',
        doubling: true,
        splitAtRank: 5,
        stat2: 'verdantSpread',
        perLevel2: 1,
        label2: 'Rank 6: Bleed spreads to 2 nearby enemies',
        nextRankUnit2: ' (Verdant Spread unlock)',
        maxRank: 6,
    },
    // regrowth: ranks 1-2 add heal targets (rank 2 = all 3 allies); ranks 3-4 unlock
    // an end-of-HoT burst delivering (rank - 2) × 25% of the total healing as a single
    // instant tick when the HoT expires. Rank 4 = 50% burst.
    regrowth: {
        label: '+1 heal target per rank',
        stat: 'targets',
        perLevel: 1,
        splitAtRank: 2,
        stat2: 'endOfHotBurst',
        perLevel2: 0.25,
        label2: 'Rank 3-4: end-of-HoT burst (25% / 50% of total heal)',
        nextRankUnit: ' target',
        nextRankUnit2: ' burst',
        maxRank: 4,
    },
    // wild_growth: AoE damage spec — same shape as Shield Bash (perLevel 0.10 doubling).
    wild_growth: { label: '+10% AoE damage (doubling)', stat: 'damageMult', perLevel: 0.10, nextRankUnit: '% damage', doubling: true, maxRank: 6 },
    // bear_form: doubles both ATK bonus and damage reduction each rank.
    // perLevel 0.07 → at rank 5 = +217% over base 20% ATK / -25% DR.
    bear_form: { label: '+ATK & DR per rank (doubling)', stat: 'bearFormBonus', perLevel: 0.07, nextRankUnit: '% ATK / DR', doubling: true, maxRank: 5 },
    // ── Shaman (Zhorak) ──
    // shaman_totems: single talent for the rotation. Ranks 1-6 add +10 % linear to
    // damage / healing / slow strength across all three schools. Rank 7 flips
    // `schoolExtras` on, unlocking: healing-totem resurrect (120s inner CD),
    // chain-lightning on the lightning totem (4 extra arcs at 50 % damage),
    // earthbind slows attack speed (-25 %) on top of movement slow.
    shaman_totems: {
        label: 'Ranks 1-6: +8 % totem damage / healing / slow (linear)',
        stat: 'totemPotency',
        perLevel: 0.08,
        nextRankUnit: '% stronger',
        doubling: false,
        splitAtRank: 6,
        stat2: 'schoolExtras',
        perLevel2: 1,
        label2: 'Rank 7: Chain Lightning + Revive + Attack-Speed Slow unlocked',
        nextRankUnit2: ' (extras unlock)',
        maxRank: 7,
    },
    // shamanic_buff: +1 second buff duration per rank (3s base → 8s at rank 5).
    shamanic_buff: {
        label: '+1 s buff duration per rank',
        stat: 'durationExtend',
        perLevel: 1000,
        nextRankUnit: 's duration',
        doubling: false,
        maxRank: 5,
    },
    // shaman_shield: +15 % shield power per rank (linear). Rank 6 = +90 %.
    shaman_shield: {
        label: '+15 % shield power per rank (linear)',
        stat: 'shieldAbsorb',
        perLevel: 0.15,
        nextRankUnit: '% power',
        doubling: false,
        maxRank: 6,
    },
    // ancestral_guidance: ranks 1-4 scale empowerment % doubling; rank 5 extends
    // the buff duration by 2 seconds. No splitAtRank — rank 5 just stacks on top.
    ancestral_guidance: {
        label: 'Ranks 1-4: +15% empowerment (doubling)',
        stat: 'ancestralBonus',
        perLevel: 0.15,
        nextRankUnit: '% stronger',
        doubling: true,
        splitAtRank: 4,
        stat2: 'ancestralDuration',
        perLevel2: 2000,
        label2: 'Rank 5: +2s buff duration',
        nextRankUnit2: 's duration',
        maxRank: 5,
    },
    // ── Cyrith (Sorcerer) ──
    // Storm Bolt — ranks 1-5 +15% damage doubling, rank 6 unlocks Chain Storm
    // (second chain target + 1.2 s stun instead of baseline 0.6 s). Mirrors
    // the death_coil / thornlash split-phase shape.
    storm_bolt: {
        label: 'Ranks 1-5: +15% bolt damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.15,
        nextRankUnit: '% damage',
        doubling: true,
        splitAtRank: 5,
        stat2: 'chainStorm',
        perLevel2: 1,
        label2: 'Rank 6: Chain Storm — +1 chain target + longer stun',
        nextRankUnit2: ' (Chain Storm unlock)',
        maxRank: 6,
    },
    // Static Field — ranks 1-3 per-tick damage doubling, rank 4 unlocks
    // Greater Field (radius 110 → 150, slow 25 % → 40 %). Splits the talent
    // so the rank-4 cap reads as a real keystone instead of just more damage.
    static_field: {
        label: 'Ranks 1-3: +25% per-tick damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.25,
        nextRankUnit: '% per tick',
        doubling: true,
        splitAtRank: 3,
        stat2: 'greaterField',
        perLevel2: 1,
        label2: 'Rank 4: Greater Field — wider radius + stronger slow',
        nextRankUnit2: ' (Greater Field unlock)',
        maxRank: 4,
    },
    // Summon Copper Golem — flat HP per rank. Linear shape (not doubling)
    // because compounding HP scaling on a frontline pet trivializes early
    // pressure. Caps at +80% HP at rank 4.
    summon_copper_golem: { label: '+20% golem max HP (linear)', stat: 'maxHpMult', perLevel: 0.20, nextRankUnit: '% golem HP', doubling: false, maxRank: 4 },
    // Arc Strike — doubling chain damage. Rank 4 unlocks a second chain
    // target via the splitAtRank pattern (mirrors regrowth's two-stage
    // shape but with the second stat unlocking the extra chain).
    arc_strike: {
        label: 'Ranks 1-3: +30% chain damage (doubling)',
        stat: 'damageMult',
        perLevel: 0.30,
        nextRankUnit: '% chain damage',
        doubling: true,
        splitAtRank: 3,
        stat2: 'extraChain',
        perLevel2: 1,
        label2: 'Rank 4: chains to 2 enemies instead of 1',
        nextRankUnit2: ' (extra chain)',
        maxRank: 4,
    },
};
/** Maximum talent points a hero can accumulate in a single run */
export const MAX_TALENT_POINTS = 12;
/** How many total talent points a hero has earned at their level */
export function getTotalTalentPoints(heroLevel) {
    return Math.min(MAX_TALENT_POINTS, Math.floor(heroLevel / TALENT_LEVEL_INTERVAL));
}
/** How many talent points have been spent */
export function getSpentTalentPoints(talentBonuses) {
    return Object.values(talentBonuses).reduce((sum, v) => sum + v, 0);
}
/** How many unspent talent points remain */
export function getAvailableTalentPoints(heroLevel, talentBonuses) {
    return getTotalTalentPoints(heroLevel) - getSpentTalentPoints(talentBonuses);
}
/** Apply a talent point to an ability. Returns the new point count for that ability, or 0 if already at maxRank. */
export function applyTalentPoint(talentBonuses, abilityId) {
    const info = TALENT_UPGRADES[abilityId];
    const current = talentBonuses[abilityId] || 0;
    if (info?.maxRank !== undefined && current >= info.maxRank)
        return current;
    talentBonuses[abilityId] = current + 1;
    return talentBonuses[abilityId];
}
/** Get the talent upgrade info for an ability (null if no upgrade defined) */
export function getTalentUpgradeInfo(abilityId) {
    return TALENT_UPGRADES[abilityId] || null;
}
/**
 * Get the cumulative bonus for a specific stat from a talent.
 *
 * For single-stat talents this is equivalent to getTalentBonusValue.
 * For two-phase split talents you can ask for either the phase-1 stat
 * (e.g. 'doubleStrike') or the phase-2 stat (e.g. 'thunderDmg') independently.
 */
export function getTalentBonusForStat(talentBonuses, abilityId, stat) {
    const info = TALENT_UPGRADES[abilityId];
    if (!info)
        return 0;
    const rank = talentBonuses[abilityId] || 0;
    if (stat === info.stat) {
        const phase1Ranks = info.splitAtRank !== undefined
            ? Math.min(rank, info.splitAtRank)
            : rank;
        // Honour the `doubling` flag so phase-1 doubling talents (death_coil,
        // thornlash, whirlwind_bludgeon) actually apply the cumulative bonus the
        // UI label advertises. Without this, getTalentTotalLabel said "+372%" but
        // combat only applied "+60%" because this branch was hard-coded linear.
        return computeBonus(phase1Ranks, info.perLevel, info.doubling);
    }
    if (stat === info.stat2 &&
        info.splitAtRank !== undefined &&
        info.stat2 !== undefined &&
        info.perLevel2 !== undefined) {
        const phase2Ranks = Math.max(0, rank - info.splitAtRank);
        return computeBonus(phase2Ranks, info.perLevel2, info.doubling2);
    }
    return 0;
}
/**
 * Get the total bonus value for an ability's primary stat.
 * For split talents this returns the phase-1 stat bonus only.
 * Use getTalentBonusForStat() when you need phase-2 as well.
 */
export function getTalentBonusValue(talentBonuses, abilityId) {
    const info = TALENT_UPGRADES[abilityId];
    if (!info)
        return 0;
    const rank = talentBonuses[abilityId] || 0;
    if (info.splitAtRank !== undefined) {
        // Phase 1 — honour the `doubling` flag so doubling talents (death_coil,
        // thornlash, whirlwind_bludgeon) match what the UI label promises. Linear
        // talents (multishot's +1 target/rank, regrowth's +1 target/rank) have
        // doubling: undefined so they still accumulate linearly via computeBonus.
        const phase1Ranks = Math.min(rank, info.splitAtRank);
        return computeBonus(phase1Ranks, info.perLevel, info.doubling);
    }
    return computeBonus(rank, info.perLevel, info.doubling);
}
//# sourceMappingURL=tomes.js.map